import React from 'react';
import { Layout, Row, Col, Typography, Card, Statistic } from 'antd';
import { 
  TeamOutlined, 
  TrophyOutlined, 
  SafetyOutlined, 
  RocketOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import './About.scss';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const About: React.FC = () => {
  return (
    <Layout className="about-layout">
      <Content>
        <div className="about-container">
          {/* Hero Section */}
          <div className="about-header">
            <div className="about-badge">About Byondcore</div>
            <Title level={1}>Empowering Businesses with <span className="highlight">Smart Solutions</span></Title>
            <Paragraph className="about-description">
              Byondcore is a comprehensive accounts & finance SAAS platform designed to transform your business operations. 
              We provide cutting-edge tools that streamline financial management, enhance productivity, and drive growth.
            </Paragraph>
          </div>

          {/* Main Content Section */}
          <Row gutter={[48, 48]} className="about-content">
            <Col xs={24} lg={12}>
              <div className="about-text">
                <Title level={2}>Complete Business Management Solution</Title>
                <Paragraph>
                  Byondcore is a robust Invoice and Billing Management Application meticulously crafted to optimize and 
                  automate the entire invoicing lifecycle for businesses. Our sophisticated software solution is tailored 
                  to simplify the creation, dispatch, and meticulous management of invoices and bills.
                </Paragraph>
                <Paragraph>
                  We understand that modern businesses need more than just basic accounting tools. That's why we've built 
                  a comprehensive platform that integrates sales, invoicing, and financial management into one seamless 
                  experience, helping you focus on what matters most - growing your business.
                </Paragraph>
                <div className="about-features">
                  <div className="feature-item">
                    <CheckCircleOutlined className="feature-check" />
                    <span>Automated Invoice Generation</span>
                  </div>
                  <div className="feature-item">
                    <CheckCircleOutlined className="feature-check" />
                    <span>Real-time Financial Analytics</span>
                  </div>
                  <div className="feature-item">
                    <CheckCircleOutlined className="feature-check" />
                    <span>Multi-platform Compatibility</span>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div className="about-image">
                <div className="placeholder-image">
                  <img src="/images/home.png" alt="Byondcore Dashboard" />
                </div>
              </div>
            </Col>
          </Row>

          {/* Statistics Section */}
          <div className="stats-section">
            <Row gutter={[32, 32]}>
              <Col xs={24} sm={8}>
                <div className="stat-card">
                  <div className="stat-icon">
                    <GlobalOutlined />
                  </div>
                  <Statistic 
                    title="Global Reach" 
                    value={5000} 
                    suffix="+"
                    valueStyle={{ color: '#577ae8', fontSize: '2.5rem', fontWeight: '700' }}
                  />
                  <Paragraph className="stat-description">
                    Businesses worldwide trust Byondcore
                  </Paragraph>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div className="stat-card">
                  <div className="stat-icon">
                    <ClockCircleOutlined />
                  </div>
                  <Statistic 
                    title="Uptime" 
                    value={99.9} 
                    suffix="%"
                    valueStyle={{ color: '#577ae8', fontSize: '2.5rem', fontWeight: '700' }}
                  />
                  <Paragraph className="stat-description">
                    Reliable service you can count on
                  </Paragraph>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div className="stat-card">
                  <div className="stat-icon">
                    <RocketOutlined />
                  </div>
                  <Statistic 
                    title="Growth" 
                    value={300} 
                    suffix="%"
                    valueStyle={{ color: '#577ae8', fontSize: '2.5rem', fontWeight: '700' }}
                  />
                  <Paragraph className="stat-description">
                    Average efficiency improvement
                  </Paragraph>
                </div>
              </Col>
            </Row>
          </div>

          {/* Values Section */}
          <div className="values-section">
            <Title level={2} className="section-title">Our Core Values</Title>
            <Paragraph className="section-description">
              These principles guide everything we do, from product development to customer support
            </Paragraph>
            <Row gutter={[24, 24]}>
              <Col xs={24} md={8}>
                <Card className="value-card">
                  <div className="value-icon">
                    <TrophyOutlined />
                  </div>
                  <Title level={4}>Excellence</Title>
                  <Paragraph>
                    We strive for excellence in everything we do, from product development to customer support. 
                    Every feature is crafted with precision and care.
                  </Paragraph>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card className="value-card">
                  <div className="value-icon">
                    <SafetyOutlined />
                  </div>
                  <Title level={4}>Security</Title>
                  <Paragraph>
                    Your data security is our top priority. We implement industry-leading security measures 
                    to protect your sensitive business information.
                  </Paragraph>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card className="value-card">
                  <div className="value-icon">
                    <RocketOutlined />
                  </div>
                  <Title level={4}>Innovation</Title>
                  <Paragraph>
                    We continuously innovate to provide cutting-edge solutions for modern businesses, 
                    staying ahead of industry trends and technology.
                  </Paragraph>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default About; 